import isEqual from 'lodash/isEqual';
import { useState, useCallback, useEffect } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
import Alert from '@mui/material/Alert';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import useModulePermission from 'src/hooks/use-module-permission';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useSnackbar } from 'src/components/snackbar';
import {
  useTable,
  getComparator,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';
//
import CourseTableToolbar from '../course-table-toolbar';
import CourseTableFiltersResult from '../course-table-filters-result';
import CourseTableRow from '../course-table-row';
import CourseGroupDialog from '../course-group-new-dialog';
import axiosInstance from 'src/utils/axios';

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [{ value: 'all', label: 'All' }];

const TABLE_HEAD = [
  { id: 'name', label: 'Group Name' },
  { id: 'target_course', label: 'Target Course' },
  { id: 'courses', label: 'Source Courses' },
  { id: 'last_updated', label: 'Last Updated' },
  { id: 'created_at', label: 'Created At' },
  { id: '', width: 88 },
];

const defaultFilters = {
  name: '',
  role: [],
  status: 'all',
  courses: [],
};

// ----------------------------------------------------------------------

export default function CourseListView() {
  const table = useTable();
  const { canGroup } = useModulePermission('course');

  const settings = useSettingsContext();

  const router = useRouter();

  const { enqueueSnackbar } = useSnackbar();

  const confirm = useBoolean();

  const [tableData, setTableData] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);

  const [isDeletingRows, setIsDeletingRows] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const fetchCourseGroups = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_HOST_API}/courses/api/course-groups/`
      );
      setTableData(response.data);
    } catch (err) {
      console.error('Failed to fetch course groups:', err);
    }
  }, []);

  useEffect(() => {
    fetchCourseGroups();
  }, [fetchCourseGroups]);

  const uniqueValues = () => {
    const allCourses = tableData.flatMap((item) => {
      const targetCourse = item.target_course ? [item.target_course.course_name] : [];
      const sourceCourses = item.courses ? item.courses.map((course) => course.course_name) : [];
      return [...targetCourse, ...sourceCourses];
    });

    return [...new Set(allCourses)];
  };

  const courses = uniqueValues();

  const [filters, setFilters] = useState(defaultFilters);

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters,
  });

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const denseHeight = table.dense ? 52 : 72;

  const canReset = !isEqual(defaultFilters, filters);

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleDeleteRow = useCallback(async () => {
    await fetchCourseGroups();
    table.onUpdatePageDeleteRow(dataInPage.length);
  }, [table, dataInPage.length, fetchCourseGroups]);

  const handleDeleteRows = useCallback(async () => {
    setIsDeletingRows(true);
    try {
      await Promise.all(
        table.selected.map((id) =>
          axiosInstance.delete(
            `${process.env.REACT_APP_HOST_API}/courses/api/course-grouping/delete/new/${id}/`
          )
        )
      );

      await fetchCourseGroups();

      table.onUpdatePageDeleteRows({
        totalRows: tableData.length,
        totalRowsInPage: dataInPage.length,
        totalRowsFiltered: dataFiltered.length,
      });

      enqueueSnackbar('Course Groups deleted successfully', { variant: 'success' });
      confirm.onFalse();
    } catch (err) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error('Failed to delete rows:', err);
    } finally {
      setIsDeletingRows(false);
    }
  }, [
    table,
    dataInPage.length,
    tableData.length,
    dataFiltered.length,
    fetchCourseGroups,
    enqueueSnackbar,
    confirm,
  ]);

  const handleEditRow = useCallback(
    (id) => {
      router.push(paths.dashboard.batch.edit(id));
    },
    [router]
  );

  const handleFilterStatus = useCallback(
    (event, newValue) => {
      handleFilters('status', newValue);
    },
    [handleFilters]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  if (!canGroup) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Course Group List"
          links={[
            { name: 'Dashboard', href: paths.dashboard.root },
            { name: 'Course Group', href: paths.dashboard.courseGroup.root },
            { name: 'Course Group List' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          You do not have permission to view this page.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Course Group List"
          links={[
            { name: 'Dashboard', href: paths.dashboard.root },
            { name: 'Course Group', href: paths.dashboard.courseGroup.root },
            { name: 'Course Group List' },
          ]}
          action={
            <>
              <Button
                variant="contained"
                onClick={handleOpenDialog}
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Course Group
              </Button>

              <CourseGroupDialog
                open={openDialog}
                onClose={handleCloseDialog}
                onSaveSuccess={fetchCourseGroups}
              />
            </>
          }
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <Card>
          <Tabs
            value={filters.status}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {STATUS_OPTIONS.map((tab) => (
              <Tab
                key={tab.value}
                iconPosition="end"
                value={tab.value}
                label={tab.label}
                icon={
                  <Label variant="filled" color="default">
                    {tab.value === 'all' && tableData.length}
                  </Label>
                }
              />
            ))}
          </Tabs>

          <CourseTableToolbar
            filters={filters}
            onFilters={handleFilters}
            courses={courses}
          />

          {canReset && (
            <CourseTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={dataFiltered.length}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <TableSelectedAction
              dense={table.dense}
              numSelected={table.selected.length}
              rowCount={tableData.length}
              onSelectAllRows={(checked) =>
                table.onSelectAllRows(
                  checked,
                  tableData.map((row) => row.id)
                )
              }
              action={
                <Tooltip title="Delete">
                  <IconButton color="primary" onClick={confirm.onTrue}>
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                </Tooltip>
              }
            />

            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tableData.length}
                  numSelected={table.selected.length}
                  onSort={table.onSort}
                  onSelectAllRows={(checked) =>
                    table.onSelectAllRows(
                      checked,
                      tableData.map((row) => row.id)
                    )
                  }
                />

                <TableBody>
                  {dataFiltered
                    .slice(
                      table.page * table.rowsPerPage,
                      table.page * table.rowsPerPage + table.rowsPerPage
                    )
                    .map((row) => (
                      <CourseTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                        onDeleteRow={handleDeleteRow}
                        onSaveSuccess={fetchCourseGroups}
                        onEditRow={() => handleEditRow(row.id)}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                  />

                  <TableNoData notFound={notFound} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={dataFiltered.length}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onRowsPerPageChange={table.onChangeRowsPerPage}
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>
      </Container>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content={
          <>
            Are you sure want to delete <strong> {table.selected.length} </strong> items?
          </>
        }
        action={
          <LoadingButton
            variant="contained"
            color="error"
            loading={isDeletingRows}
            onClick={handleDeleteRows}
          >
            Delete
          </LoadingButton>
        }
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({ inputData, comparator, filters }) {
  const { name, status, role, courses } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (courses.length) {
    inputData = inputData.filter((row) => {
      const targetCourseName = row.target_course?.course_name;
      const sourceCoursesNames = row.courses?.map((course) => course.course_name) || [];
      return courses.some(
        (selectedCourse) =>
          targetCourseName === selectedCourse || sourceCoursesNames.includes(selectedCourse)
      );
    });
  }

  if (name) {
    inputData = inputData.filter(
      (batch) => batch.name.toLowerCase().indexOf(name.toLowerCase()) !== -1
    );
  }

  if (status !== 'all') {
    inputData = inputData.filter((user) => user.status === status);
  }

  if (role.length) {
    inputData = inputData.filter((user) => role.includes(user.role));
  }

  return inputData;
}